<template>
  <div
    :class="{
      'footer-bar px-3 py-2 justify-self-end border-top': getFooterSettings($route).enabled,
      'd-none': hasNoTranslations,
    }"
  >
    <div class="row m-0">
      <div class="pr-2">
        <RsButton
          v-if="getFooterSettings($route).hasTranslationsBtn && !$rsGlobals.globalTranslationsBtnConfig.hide"
          :disabled="$rsGlobals.globalTranslationsBtnConfig.disabled"
          :variant="$rsGlobals.globalTranslationsBtnConfig.variant"
          @click="handleTranslationBtnClicked()"
        >
          {{ $rsGlobals.globalTranslationsBtnConfig.text || $ts('actions.translations') }}
        </RsButton>
        <RsToggleTranslation
          v-if="getFooterSettings($route).hasTranslations && $rsGlobals.globalTranslationsConfig"
          :options="getFooterLanguageSettings()"
          use-select
        />
      </div>
      <div
        v-if="getFooterSettings($route).hasCheckBox"
        class="content-checkbox "
      >
        <RsFormCheckbox
          id="receives_invoices"
          v-model="$rsGlobals.globalCheckBoxConfig.checkBoxValue"
          class="justify-self-start mb-0"
          :true-value="$rsGlobals.globalCheckBoxConfig.trueValue"
          :false-value="$rsGlobals.globalCheckBoxConfig.falseValue"
          :label="$rsGlobals.globalCheckBoxConfig.text"
          @change="handleCheckBoxClicked()"
        />
      </div>
      <div
        v-if="getFooterSettings($route).hasBottomText"
        class="footer-text mt-2"
      >
        {{ $rsGlobals.footerBottomText }}
      </div>
      <div id="delete-button-wrapper">
        <RsButton
          v-if="getFooterSettings($route).hasDelete && !$rsGlobals.globalDeleteBtnConfig.hide"
          :variant="$rsGlobals.globalDeleteBtnConfig.variant || 'error'"
          :disabled="$rsGlobals.globalDeleteBtnConfig.disabled"
          @click="handleDeleteClicked()"
        >
          <RsIcon
            v-if="$rsGlobals.globalDeleteBtnConfig.icon"
            :icon="$rsGlobals.globalDeleteBtnConfig.icon"
            class="mr-1"
            :weight="$rsGlobals.globalDeleteBtnConfig.iconWeight || 'far'"
          />
          {{ $rsGlobals.globalDeleteBtnConfig.text || $ts('actions.delete') }}
        </RsButton>
      </div>
      <RsTooltip
        :title="!$rsGlobals.globalDeleteBtnConfig.disableToolTip ? $rsGlobals.globalDeleteBtnConfig.toolTipText : ''"
        position="top"
        :target="`delete-button-wrapper`"
      />
      <div class="flex-grow-1" />
      <div
        v-if="getFooterSettings($route).hasLastUpdated"
        class="pt-2 pr-3 date-update"
      >
        {{ $ts('dates.lastUpdated') }}: {{ $rsGlobals.footerLastUpdated }}
      </div>
      <div
        v-if="getFooterSettings($route).hasCustomText"
        class="pt-2 pr-3"
      >
        {{ $rsGlobals.footerCustomText }}
      </div>

      <div>
        <RsButton
          v-for="key in Object.keys(extraButtons)"
          :id="`toolbar_${key}`"
          :key="`toolbar_${key}`"
          v-bind="extraButtons[key].buttonProps"
          v-rs-tooltip.hover="extraButtons[key].tooltip"
          class="mr-2"
          @click="handlExtraButtonClick(extraButtons[key])"
        >
          {{ typeof extraButtons[key].buttonText === 'function' ? extraButtons[key].buttonText() : extraButtons[key].buttonText }}
        </RsButton>
      </div>

      <div id="save-button-wrapper">
        <RsButton
          v-if="getFooterSettings($route).hasSave && !$rsGlobals.globalSaveBtnConfig.hide"
          :variant="$rsGlobals.globalSaveBtnConfig.variant || 'primary'"
          :disabled="$rsGlobals.globalSaveBtnConfig.disabled"
          :class="$rsGlobals.globalSaveBtnConfig.icon ? 'pl-0' : ''"
          @click="handleSaveClicked()"
        >
          <RsIcon
            v-if="$rsGlobals.globalSaveBtnConfig.icon"
            :icon="$rsGlobals.globalSaveBtnConfig.icon"
            class="ml-2 mr-1"
            :weight="$rsGlobals.globalSaveBtnConfig.iconWeight || 'fas'"
          />

          {{ $rsGlobals.globalSaveBtnConfig.text || $ts('actions.save') }}
        </RsButton>
      </div>
      <RsTooltip
        :title="!$rsGlobals.globalSaveBtnConfig.disableToolTip ? ($rsGlobals.globalSaveBtnConfig.toolTipText || $ts('actions.save')) : ''"
        position="top"
        :target="`save-button-wrapper`"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import appMixin from '@/mixins/app-mixin';
import { IRouteMetaFooterExtraButton, ISelectOption } from '@/shared';
import RsButton from './_global/RsButton.vue';

export default defineComponent({
  name: 'RsPartialAppFooter',

  mixins: [ appMixin ],

  data() {
    return {
      extraButtons: {} as Record<string, IRouteMetaFooterExtraButton>,
    };
  },

  computed: {
    /**
     * Wow, we're really making sure there are no translations here...
     */
    hasNoTranslations(): boolean {
      return !!(this.getFooterSettings(this.$route).hasTranslations)
        && this.$rsGlobals.$clientLanguageOptions.length === 1
        && (this.getFooterSettings(this.$route).enabled)
        && !(this.getFooterSettings(this.$route).hasSave)
        && !(this.getFooterSettings(this.$route).hasDelete)
        && !(this.getFooterSettings(this.$route).hasBottomText);
    },
  },

  watch: {
    '$route.path': {
      immediate: true,
      handler() {
        this.setExtraButtons();
      },
    },
  },

  mounted() {
    this.$eventBus.on('set-footer-extra-button', this.handleSetExtraButton as any);
  },

  methods: {
    /**
     * Stores extra buttons in local state, as they are dynamically created.
     * The state of extra buttons will change when route changes (see `$route.path` watcher).
     * Extra buttons are configured via router entries in `src/router` and assigned handlers
     * in component pages.
     */
    setExtraButtons() {
      this.extraButtons = {};

      // Need to use `$set` on object types for Vue to register new keys.
      Object.entries(this.getFooterSettings(this.$route).extraButtons ?? {}).forEach(([key, value]) => {
        this.extraButtons[key] = value;
      });
    },

    /**
     * Returns language options for select dropdown for translations
     * This might be legacy and unused currently. Not removing until I'm sure [Alex]
     */
    getFooterLanguageSettings(): ISelectOption[] | undefined {
      return this.getFooterSettings(this.$route)?.languageOptions?.();
    },

    /**
     * Sets button props, click handler, and tooltip based on passed `buttonKey` property.
     * This is called when our `eventBus` catches a `set-footer-extra-button` event that is emitted
     * by page components.
     */
    handleSetExtraButton(payload: Partial<IRouteMetaFooterExtraButton> & { buttonKey: string }) {
      if (!this.extraButtons[payload.buttonKey]) {
        return;
      }

      this.extraButtons[payload.buttonKey] = {
        ...this.extraButtons[payload.buttonKey],
        ...payload,
        buttonProps: {
          ...this.extraButtons[payload.buttonKey].buttonProps ?? {},
          ...payload.buttonProps ?? {},
        },
      };
    },

    /**
     * Checks for and executes an extra button's click handler.
     * @param button
     */
    handlExtraButtonClick(button: IRouteMetaFooterExtraButton) {
      if (button.buttonHandler) {
        button.buttonHandler();
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

.footer-bar {
  z-index: 1000;
  background: $white;
}

#save-button-wrapper {
  .rs-icon {
    font-size: 16px;
    color: white !important;
  }
}
</style>
